import Button from '@rio-cloud/rio-uikit/Button';
import ButtonToolbar from '@rio-cloud/rio-uikit/ButtonToolbar';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import SingleUpBarChartWithToggle from '@/charts/chartsComponents/SingleUpBarChartWithToggle';
import {
  Container,
  Content,
  SwitcherContainer,
  TableContainer,
} from '@/components/molecule/TableGraphSwitcher/TableGraphSwitcher.styles';
import TableActionBar from '@/components/organism/TableActionBar/TableActionBar';
import { useAppDispatch, useAppSelector } from '@/configuration/setup/hooks';
import { TDataVisualization } from '@/services/threadApi/types';
import { selectMessageById, updateMessageDataVisualization } from '@/store/chat/chat.slice';
import { selectKPIDataVisualization } from '@/store/chat/KPIDataVisualizationPreference/KPIDataVisualizationPreference.slice';
import { TKPIDataVisualisationMode } from '@/store/chat/KPIDataVisualizationPreference/types';
import { formatDataFromLocale } from '@/utils/formatDataFromLocale/formatDataFromLocale';

import UiKitTable from '../UiKitTable/UiKitTable';

type TableGraphSwitcherProps = {
  data: Record<string, unknown>[] | Record<string, unknown> | null | undefined;
  message: string;
  showActions: boolean;
  data_visualization: TDataVisualization | null;
  messageId: string;
};

const TableGraphSwitcher = ({
  data,
  message,
  showActions,
  data_visualization, // TODO: Reduce prop drilling here since data visualization can be obtained through the messageId
  messageId,
}: TableGraphSwitcherProps) => {
  const dispatch = useAppDispatch();
  const { language } = useAppSelector(selectMessageById(messageId));

  const tempDataVisualisation = useAppSelector(selectKPIDataVisualization(messageId));

  const parsedTable = formatDataFromLocale(data as Record<string, unknown>[], language);

  const updateDataVisualization = useCallback(
    (mode: TKPIDataVisualisationMode) => {
      dispatch(
        updateMessageDataVisualization({
          messageId,
          data_visualization: {
            default_data_visualization: mode,
            x_axis: data_visualization?.x_axis ?? '',
          },
        }),
      );
    },
    [dispatch, messageId, data_visualization?.x_axis],
  );

  const xAxis: string | null = data_visualization?.x_axis ?? null;
  const isChartSuitable = Boolean(xAxis);

  const { isGraph, isTable } = useMemo(() => {
    // INFO: Ensures that previous data visualization mode stays consistent when new message is sent
    const data = tempDataVisualisation?.data ?? data_visualization;
    const mode = data?.default_data_visualization === 'graph' ? 'graph' : 'table';

    return {
      isGraph: mode === 'graph',
      isTable: mode === 'table',
    } as const;
  }, [tempDataVisualisation]);

  return (
    <Container>
      <SwitcherContainer>
        <ButtonToolbar>
          <div className="btn-group">
            <Button iconName="icon-table-view" active={isTable} onClick={() => updateDataVisualization('table')}>
              <FormattedMessage id="chatPage.SpeechBubble.toggle.table" />
            </Button>
            <Button
              iconName="icon-stats"
              active={isGraph}
              onClick={() => updateDataVisualization('graph')}
              disabled={!isChartSuitable}
            >
              <FormattedMessage id="chatPage.SpeechBubble.toggle.chart" />
            </Button>
          </div>
        </ButtonToolbar>
      </SwitcherContainer>

      <Content>
        {isTable ? (
          <>
            <TableContainer>
              <UiKitTable data={parsedTable} xAxis={xAxis} messageId={messageId} />
            </TableContainer>
            {showActions ? <TableActionBar message={message} /> : null}
          </>
        ) : (
          <SingleUpBarChartWithToggle data={data as Record<string, unknown>[]} messageId={messageId} />
        )}
      </Content>
    </Container>
  );
};

export default TableGraphSwitcher;
